// frontend/src/utils/useCompany.js
//
// The trader's published identity, fetched once and shared.
//
// Everything here used to be typed into the pages by hand — the support address
// in Terms.js, a different one in PrivacyPolicy.js, a third in Contact.js, and
// all three on a domain that turned out to have no DNS records. One source, read
// from the backend's own configuration, is what stops those three drifting apart
// again, and it means the identity appears everywhere the moment the company
// registration is filled into the backend .env — no rebuild, no second edit.
import { useEffect, useState } from 'react';
import { API_BASE } from '../config';
import { setSupportEmail } from '../i18n';

// Used before the request resolves and if it fails. It carries only what is true
// without configuration: the trading name and a contact address that receives
// mail. It deliberately does NOT carry an invented legal name or address.
export const COMPANY_FALLBACK = {
  // Whether the answer has come back yet, as distinct from what the answer is.
  // `configured: false` is also the pre-fetch state, so a caller that branches
  // on it alone flashes "not configured" for the length of one request even
  // when the identity is complete.
  loaded: false,
  configured: false,
  tradingName: 'GlamGo',
  legalName: null,
  registrationNumber: null,
  vatNumber: null,
  vatRegistered: false,
  address: null,
  email: 'support@glamgo.hair',
  phone: null,
  termsVersion: null,
};

// Module-level so the Terms page, the Privacy page and every footer share a
// single request rather than one each.
let cached = null;
let inFlight = null;

function load() {
  if (cached) return Promise.resolve(cached);
  if (!inFlight) {
    inFlight = fetch(`${API_BASE}/company`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data === 'object') cached = { ...COMPANY_FALLBACK, ...data, loaded: true };
        // The locale files write to support@{{supportEmail}} in the seven
        // places that ask a stuck customer to contact us. They are rendered by
        // components that have no reason to depend on the company record, so
        // the address reaches them through i18next rather than through props.
        setSupportEmail((cached || COMPANY_FALLBACK).email);
        inFlight = null;
        return cached || COMPANY_FALLBACK;
      })
      .catch(() => {
        inFlight = null;
        return COMPANY_FALLBACK;
      });
  }
  return inFlight;
}

export function useCompany() {
  const [company, setCompany] = useState(cached || COMPANY_FALLBACK);
  useEffect(() => {
    let alive = true;
    load().then((c) => { if (alive) setCompany(c); });
    return () => { alive = false; };
  }, []);
  return company;
}
